import { useState, type KeyboardEvent } from 'react';
import { Link } from 'react-router-dom';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { getErrorMessage } from '../../lib/errors';
import { formatRelativeTime } from '../../lib/format';
import type { AssistantThreadRead } from '../../types/api';
import { threadLabel, type AssistantThreadsResult } from './useAssistantThreads';

const GLYPH_PENCIL = String.fromCodePoint(0xe1f9); // pencil
const GLYPH_TRASH = String.fromCodePoint(0xe18e); // trash-2

interface AssistantThreadListProps {
  threads: AssistantThreadsResult;
  activeThreadId: string | null;
  onNew: () => void;
  /** A row link was followed; the page closes its narrow-screen drawer. */
  onFollow: () => void;
  /** The open chat was deleted: the newest one left, or null. */
  onRemoved: (nextThreadId: string | null) => void;
}

/**
 * Every assistant chat for this brand, newest activity first. Rows are real
 * links, so a chat opens on its own URL; rename and delete sit on the open
 * chat only. Deleting asks first: a conversation has no undo.
 */
export function AssistantThreadList({
  threads,
  activeThreadId,
  onNew,
  onFollow,
  onRemoved,
}: AssistantThreadListProps) {
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameError, setRenameError] = useState<string | null>(null);

  const [pendingDelete, setPendingDelete] = useState<AssistantThreadRead | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  function beginRename(thread: AssistantThreadRead) {
    setRenamingId(thread.id);
    setRenameDraft(thread.title ?? '');
    setRenameError(null);
  }

  /** Enter and blur both commit; a blank or unchanged name is "never mind". */
  async function commitRename() {
    if (renamingId === null || isRenaming) return;
    const thread = threads.threads.find((candidate) => candidate.id === renamingId);
    const title = renameDraft.trim();
    if (thread === undefined || title.length === 0 || title === thread.title) {
      setRenamingId(null);
      return;
    }
    setIsRenaming(true);
    setRenameError(null);
    try {
      await threads.rename(renamingId, title);
      setRenamingId(null);
    } catch (caught) {
      setRenameError(getErrorMessage(caught, 'That chat could not be renamed.'));
    } finally {
      setIsRenaming(false);
    }
  }

  function onRenameKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Escape') {
      event.preventDefault();
      setRenamingId(null);
      setRenameError(null);
      return;
    }
    if (event.key !== 'Enter' || event.nativeEvent.isComposing) return;
    event.preventDefault();
    void commitRename();
  }

  async function confirmDelete() {
    if (pendingDelete === null) return;
    const remaining = threads.threads.filter((thread) => thread.id !== pendingDelete.id);
    const wasActive = pendingDelete.id === activeThreadId;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await threads.remove(pendingDelete.id);
      setPendingDelete(null);
      if (wasActive) onRemoved(remaining[0]?.id ?? null);
    } catch (caught) {
      setDeleteError(getErrorMessage(caught, 'That chat could not be deleted.'));
    } finally {
      setIsDeleting(false);
    }
  }

  return (
    <aside className="assistant-threads" aria-label="Assistant chats">
      <div className="assistant-threads__head">
        <h2 className="assistant-threads__title">Chats</h2>
        <button
          className="button button--ghost assistant-threads__new"
          type="button"
          onClick={onNew}
        >
          New chat
        </button>
      </div>

      {threads.error ? (
        <p className="form__error" role="alert">
          {threads.error}
        </p>
      ) : null}

      {threads.isLoading ? (
        <p className="assistant-threads__note" role="status">
          Loading your chats
        </p>
      ) : threads.threads.length === 0 ? (
        <p className="assistant-threads__note">No chats yet.</p>
      ) : (
        <ul className="assistant-threads__list">
          {threads.threads.map((thread) => {
            const isActive = thread.id === activeThreadId;
            const label = threadLabel(thread);
            return (
              <li
                className={
                  isActive ? 'assistant-thread assistant-thread--active' : 'assistant-thread'
                }
                key={thread.id}
              >
                {renamingId === thread.id ? (
                  <>
                    <label className="visually-hidden" htmlFor={`rename-${thread.id}`}>
                      Rename chat
                    </label>
                    <input
                      className="field__input assistant-thread__rename"
                      id={`rename-${thread.id}`}
                      type="text"
                      value={renameDraft}
                      maxLength={120}
                      autoFocus
                      disabled={isRenaming}
                      onChange={(event) => setRenameDraft(event.target.value)}
                      onKeyDown={onRenameKeyDown}
                      onBlur={() => void commitRename()}
                    />
                    {renameError ? (
                      <p className="form__error" role="alert">
                        {renameError}
                      </p>
                    ) : null}
                  </>
                ) : (
                  <>
                    <Link
                      className="assistant-thread__link"
                      to={`/assistant/${thread.id}`}
                      aria-current={isActive ? 'page' : undefined}
                      onClick={onFollow}
                    >
                      <span className="assistant-thread__label">{label}</span>
                      <span className="assistant-thread__time">
                        {formatRelativeTime(thread.updated_at)}
                      </span>
                    </Link>
                    {isActive ? (
                      <div className="assistant-thread__actions">
                        <button
                          className="assistant-thread__action"
                          type="button"
                          title="Rename"
                          aria-label={`Rename ${label}`}
                          onClick={() => beginRename(thread)}
                        >
                          <span className="assistant-glyph" aria-hidden="true">
                            {GLYPH_PENCIL}
                          </span>
                        </button>
                        <button
                          className="assistant-thread__action assistant-thread__action--danger"
                          type="button"
                          title="Delete"
                          aria-label={`Delete ${label}`}
                          onClick={() => {
                            setDeleteError(null);
                            setPendingDelete(thread);
                          }}
                        >
                          <span className="assistant-glyph" aria-hidden="true">
                            {GLYPH_TRASH}
                          </span>
                        </button>
                      </div>
                    ) : null}
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {pendingDelete !== null ? (
        <ConfirmDialog
          title="Delete this chat"
          message={`“${threadLabel(pendingDelete)}” and everything said in it will be deleted. Anything you already applied stays on the calendar.`}
          confirmLabel="Delete chat"
          busyLabel="Deleting"
          isBusy={isDeleting}
          error={deleteError}
          onConfirm={() => void confirmDelete()}
          onDismiss={() => {
            setPendingDelete(null);
            setDeleteError(null);
          }}
        />
      ) : null}
    </aside>
  );
}
